import React from 'react';
import moment from 'moment';
import { withStyles } from '@material-ui/core/styles';
import { withTranslation } from 'react-i18next';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import CalendarToday from '@material-ui/icons/CalendarToday';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Slide from '@material-ui/core/Slide';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import DateRangePicker from './DateRangePicker';

const styles = theme => ({
  headerClickable: {
    cursor: 'pointer',
  },
  headerHeading: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  headerSubheading: {
    fontSize: 14,
    fontWeight: 'bold',
    '& span': {
      fontWeight: 'normal',
    },
  },
  headerSection: {
    marginBottom: 20,
  },
  calendarButton: {
    backgroundColor: '#f56a12',
    color: '#fff',
    padding: '10px 16px',
    '&:hover': {
      backgroundColor: '#C24C03',
    },
  },
  dialogCloseButton: {
    position: 'absolute',
    top: 0,
    right: 0,
    margin: 8,
    zIndex: 9999,
  },
  dialogContent: {
    position: 'relative',
    padding: 50,
    width: '100%',
    minWidth: isMobileApp
      ? 'auto'
      : 320,
    boxSizing: 'border-box',
    margin: '0 auto',
  },
  dialogContentList: {
    '& div': {
      textAlign: 'center',
      cursor: 'pointer',
    },
  },
});

const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

class CalendarHeader extends React.Component {
  constructor(props) {
    super(props);

    const {
      t,
    } = props;

    const ranges = {};

    if (props.includeFutureRanges) {
      ranges[t('Next 30 days')] = [
        moment(),
        moment().add(29, 'days'),
      ];
      ranges[t('Next 90 days')] = [
        moment(),
        moment().add(89, 'days'),
      ];
      ranges[t('Next week')] = [
        moment().add(7, 'days').startOf('week'),
        moment().add(7, 'days').endOf('week'),
      ];
    }

    ranges[t('This week')] = [
      moment().startOf('week'),
      moment().endOf('week'),
    ];
    ranges[t('Last week')] = [
      moment().subtract(1, 'weeks').startOf('week'),
      moment().subtract(1, 'weeks').endOf('week'),
    ];

    if (!props.includeFutureRanges) {
      ranges[t('This month')] = [
        moment().startOf('month'),
        moment(),
      ];
      ranges[t('Last month')] = [
        moment().subtract(1, 'months').startOf('month'),
        moment().subtract(1, 'months').endOf('month'),
      ];
    }
    ranges[t('Last 30 days')] = [
      moment().subtract(30, 'days'),
      moment(),
    ];
    ranges[t('Last 90 days')] = [
      moment().subtract(90, 'days'),
      moment(),
    ];
    ranges[t('Since August')] = [
      moment().month() >= 8
        ? moment([moment().year(), 7, 1])
        : moment([moment().year() - 1, 7, 1]),
      moment(),
    ];
    ranges[t('Custom')] = 'custom';

    this.state = {
      dateStart: props.dateStart,
      dateEnd: props.dateEnd,
      isDialogOpen: false,
      showDateRangePicker: false,
      ranges,
      selectedRangeKey: t('Custom'),
    };

    this.onCalendarButtonClick = this.onCalendarButtonClick.bind(this);
    this.onDialogClose = this.onDialogClose.bind(this);
    this.onRangeClick = this.onRangeClick.bind(this);
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      this.state.dateStart !== this.props.dateStart ||
      this.state.dateEnd !== this.props.dateEnd
    ) {
      this.setState({
        dateStart: this.props.dateStart,
        dateEnd: this.props.dateEnd,
      });
    }
  }

  onCalendarButtonClick() {
    this.setState({
      isDialogOpen: true,
      showDateRangePicker: false,
    });
  }

  onDialogClose() {
    this.setState({
      isDialogOpen: false,
      showDateRangePicker: false,
    });
  }

  onRangeClick(key, event) {
    event.preventDefault();

    const {
      ranges,
    } = this.state;
    const range = ranges[key];

    if (range === 'custom') {
      this.setState({
        showDateRangePicker: true,
      });
    } else {
      if (this.props.onRangeChanged && Array.isArray(range) && range.length === 2) {
        this.props.onRangeChanged(range);
      }

      this.setState({
        isDialogOpen: false,
      });
    }

    this.setState({
      selectedRangeKey: key,
    });
  }

  render() {
    const {
      classes,
      t,
      dateText,
    } = this.props;
    const {
      isDialogOpen,
      ranges,
      dateStart,
      dateEnd,
      showDateRangePicker,
      selectedRangeKey,
    } = this.state;

    const isLoading = !dateStart || !dateEnd;

    const dateStartString = !dateStart
      ? null
      : (typeof dateStart === 'string'
        ? moment.parseZone(dateStart).format()
        : dateStart.format()
      );
    const dateEndString = !dateEnd
      ? null
      : (typeof dateEnd === 'string'
        ? moment.parseZone(dateEnd).format()
        : dateEnd.format()
      );
    const startDate = moment.parseZone(dateStartString);
    const endDate = moment.parseZone(dateEndString);

    let displayDateRange = '';
    let daysDifference = 0;

    if (!isLoading) {
      displayDateRange = `${startDate.format(dateFormat)} - ${endDate.format(dateFormat)}`;
      daysDifference = endDate.diff(startDate, 'days') + 1;
    }

    let displayDateRangeHeading = selectedRangeKey;
    if (displayDateRangeHeading === t('Custom')) {
      displayDateRangeHeading += t(' - showing {daysDifference} days')
        .replace('{daysDifference}', daysDifference);
    }

    return (
      <div className="calendar-header">
        <Grid
          className={classes.headerSection}
          justify="flex-start"
          container
          spacing={4}
        >
          <Grid item>
            <Button
              variant="contained"
              onClick={this.onCalendarButtonClick}
              className={`calendar-header-open-button ${classes.calendarButton}`}
            >
              <CalendarToday />
            </Button>
          </Grid>
          <Grid item>
            {isLoading && (
              <Typography className={classes.headerHeading}>
                {t('Loading...')}
              </Typography>
            )}
            {!isLoading && (
              <div onClick={this.onCalendarButtonClick} className={classes.headerClickable}>
                <Typography className={classes.headerHeading}>
                  {displayDateRangeHeading}
                </Typography>
                <Typography className={classes.headerSubheading}>
                  {dateText}
                  <span>&nbsp;</span>
                  <span>{displayDateRange}</span>
                </Typography>
              </div>
            )}
          </Grid>
        </Grid>
        <Dialog
          fullScreen={isMobileApp}
          scroll="paper"
          onClose={this.onDialogClose}
          open={isDialogOpen}
          TransitionComponent={Transition}
          className="calendar-dialog"
        >
          <IconButton
            aria-label={t('Close')}
            className={`calendar-dialog-close-button ${classes.dialogCloseButton}`}
            onClick={this.onDialogClose}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent className={classes.dialogContent}>
            <List
              component="nav"
              className={classes.dialogContentList}
            >
              {Object.keys(ranges).map(key => (
                <ListItem
                  key={key}
                  onClick={this.onRangeClick.bind(this, key)}
                  selected={key === selectedRangeKey}
                  alignItems="center"
                >
                  <ListItemText primary={key} />
                </ListItem>
              ))}
            </List>
            {!isLoading && (
              <div style={{
                textAlign: 'center',
                display: showDateRangePicker || selectedRangeKey === t('Custom')
                  ? 'block'
                  : 'none',
                }}
              >
                <DateRangePicker
                  format={dateFormat}
                  shouldDisableDate={(day) => {
                    if (this.props.includeFutureRanges) {
                      return false;
                    }
                    return day.isAfter(moment().add(1, 'day'));
                  }}
                  value={[startDate, endDate]}
                  onChange={(dates) => {
                    if (this.props.onRangeChanged && Array.isArray(dates) && dates.length === 2) {
                      this.props.onRangeChanged(dates);
                    }

                    this.setState({
                      isDialogOpen: false,
                    });
                  }}
                  cancelLabel={false}
                />
              </div>
              )
            }
          </DialogContent>
        </Dialog>
      </div>
    );
  }
}

export default withStyles(styles)(withTranslation()(CalendarHeader));
